import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { Table } from '../../Table';
import { FiPlusCircle, FiTrash2 } from 'react-icons/fi';
import { InputValidators } from '../../../utils/validators/InputValidators';

export const CardCobroVenta = ({ listProducts, listPaymentMethods, onUpdate, precioIncluyeIVA = false }) => {


    // Manejar eliminación de producto
    const handleDeleteRow = (id) => {
        Swal.fire({
            icon: 'warning',
            title: 'Atención',
            text: `Está por eliminar un producto de listado de compra, ¿desea continuar con esta acción?`,
            showCancelButton: true,
            confirmButtonText: 'Aceptar',
            cancelButtonText: 'Cancelar',
            customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-secondary ms-2' },
            buttonsStyling: false
        }).then((result) => {
            if (result.isConfirmed) {
                const updated = listProducts.filter(p => p.ID !== id);

                onUpdate?.({ productos: updated });
            }
        });
    };

    // Manejar cambio de cantidad
    const handleChangeCantidad = (id, value) => {
        const updated = listProducts.map(p => {
            if (p.ID === id) {
                const cantidad = Math.max(1, Math.min(Number(value), p.EXISTENCIA));
                return { ...p, CANTIDAD: cantidad };
            }
            return p;
        });
        onUpdate?.({ productos: updated });
        // const updated = listProducts.map(p => p.ID === id ? { ...p, CANTIDAD: Number(value) } : p);
        // onUpdate?.({ productos: updated });
    };


    // Cálculos automáticos 
    // Sin bandera quedaron obsoletos
    // const subtotal = useMemo(() => listProducts.reduce((acc, p) => acc + p.CANTIDAD * p.COSTO_PUBLICO, 0), [listProducts]);
    // const iva = useMemo(() => subtotal * 0.16, [subtotal]); // 16% IVA ejemplo
    // const total = useMemo(() => subtotal + iva, [subtotal, iva]);

    const { subtotal, iva, total } = useMemo(() => {
        const totalBruto = listProducts.reduce((acc, p) => acc + p.CANTIDAD * p.COSTO_PUBLICO, 0);

        if (precioIncluyeIVA) {
            const ivaCalc = totalBruto - totalBruto / 1.16;
            const subtotalCalc = totalBruto - ivaCalc;
            return { subtotal: subtotalCalc, iva: ivaCalc, total: totalBruto };
        } else {
            const ivaCalc = totalBruto * 0.16;
            const subtotalCalc = totalBruto;
            const totalCalc = subtotalCalc + ivaCalc;
            return { subtotal: subtotalCalc, iva: ivaCalc, total: totalCalc };
        }
    }, [listProducts, precioIncluyeIVA]);



    // Cobro
    const { register, handleSubmit, reset, getValues, setValue, watch, formState: { errors } } = useForm({ model: 'all', defaultValues: { metodo: "", comentarios: "", monto: "" } });
    const [pagos, setPagos] = useState([]);

    const onSubmit = (data) => {
        const nuevoPago = {
            metodo: data.metodo,
            comentarios: data.comentarios,
            monto: Number(data.monto)
        };

        setPagos([...pagos, nuevoPago]);
        reset();
    };

    const handleDeleteRowPago = (row) => {
        Swal.fire({
            icon: 'warning',
            title: 'Atención',
            text: `Está por eliminar una forma de pago, ¿desea continuar con esta acción?`,
            showCancelButton: true, confirmButtonText: 'Aceptar', cancelButtonText: 'Cancelar',
            customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-secondary ms-2' }, buttonsStyling: false
        }).then((result) => {
            if (result.isConfirmed) {
                setPagos(prev => prev.filter(p =>
                    !(p.metodo === row.metodo && p.comentarios === row.comentarios && p.monto === row.monto)
                ));
            }
        });
    }

    const totalPagado = pagos.reduce((acc, p) => acc + p.monto, 0);
    const cambio = totalPagado > total ? totalPagado - total : 0;
    const faltante = total > totalPagado ? total - totalPagado : 0;


    const columns = [
        {
            header: 'ACCIONES',
            dataIndex: 'acciones',
            classHeader: 'text-center',
            classRow: 'text-center',
            renderer: (value, row) => (
                <button type="button" className="btn btn-outline-danger  border-0" onClick={() => handleDeleteRowPago(row)}>
                    <FiTrash2 size={24} />
                </button>
            )
        },
        {
            header: "Forma de pago",
            dataIndex: "metodo",
            renderer: (value) => {
                // Busca el nombre de la forma de pago en la lista
                const pago = listPaymentMethods.find(p => p.ID === value || p.NOMBRE === value);
                return pago ? pago.NOMBRE : value; // si no lo encuentra, mostrar el valor original
            }
        },
        {
            header: "Comentarios",
            dataIndex: "comentarios"
        },
        {
            header: "Monto",
            dataIndex: "monto",
            classRow: "text-end",
            classHeader: 'text-end',
            renderer: (value) => Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value),

        }
    ];


    const totalRounded = Number(total.toFixed(2));
    const totalPagadoRounded = Number(totalPagado.toFixed(2));
    const cambioRounded = Number(cambio.toFixed(2));
    const faltanteRounded = Number(faltante.toFixed(2));
    //RETORNO DE DATO
    useEffect(() => {
        onUpdate?.({
            productos: listProducts,
            pagos,
            total: totalRounded,
            totalPagado: totalPagadoRounded,
            cambio: cambioRounded,
            faltante: faltanteRounded,
            subtotal: Number(subtotal).toFixed(2),
            iva: Number(iva.toFixed(2)),
        });
    }, [listProducts, iva, pagos, subtotal, totalRounded, totalPagadoRounded, cambioRounded, faltanteRounded,]);



    return (
        <div className="mt-2">
            <div className="card-header border-bottom border-top">
                <h5 className="card-title mb-0 fw-bold text-black">Listado de compra</h5>
            </div>
            <div className="card-body">
                <div className='table-responsive'>
                    <table className="table table-stripped table-sm">
                        <tbody>
                            <tr>
                                <td colSpan={6} className="p-0 border-0">
                                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                                        <table className="table mb-0 table-sm">
                                            <thead>
                                                <tr className='th-sticky'>
                                                    <th scope="col">ACCION</th>
                                                    <th scope="col">SKU</th>
                                                    <th scope="col">PRODUCTO</th>
                                                    <th scope="col" className="text-end">COSTO</th>
                                                    <th scope="col" className="text-center">CANTIDAD</th>
                                                    <th scope="col" className="text-center">ACUMULADO</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    listProducts.length === 0 ?
                                                        (
                                                            <tr colSpan={6}>
                                                                <td> No se han agregado productos para su compra</td>
                                                            </tr>
                                                        ) :
                                                        (
                                                            listProducts.map((item) => (
                                                                <tr key={item.ID}>
                                                                    <td style={{ width: 50 }}><button type="button" className="btn btn-outline-danger border-0" onClick={() => handleDeleteRow(item.ID)}> <FiTrash2 size={24} /></button> </td>
                                                                    <td style={{ width: 70 }} className="text-start">{item.SKU}</td>
                                                                    <td className="">{item.NOMBRE}: {item.DESCRIPCION}</td>
                                                                    <td style={[]} className="text-end">{Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(item.COSTO_PUBLICO)}</td>
                                                                    <td style={{ width: 80 }} className=""> <input type="number" className="form-control text-end" min={1} value={item.CANTIDAD} onChange={(e) => handleChangeCantidad(item.ID, e.target.value)} /> </td>
                                                                    <td style={{ width: 150 }} className=""> <input type="number" className="form-control text-end" value={item.CANTIDAD * item.COSTO_PUBLICO} disabled /> </td>
                                                                </tr>
                                                            ))
                                                        )
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                            </tr>

                        </tbody>
                        <tfoot>
                            <tr className='ms-2'>
                                <td colSpan={5} className="fw-bold text-end border-0">
                                    SUBTOTAL:
                                </td>
                                <td style={{ width: 150 }} className="border-0"> <input type="number" className="form-control text-end" value={subtotal.toFixed(2)} disabled /> </td>
                            </tr>
                            <tr>
                                <td colSpan={5} className="fw-bold text-end border-0">
                                    IVA:
                                </td>
                                <td style={{ width: 150 }} className="border-0"> <input type="number" className="form-control text-end" id="tIVA" value={iva.toFixed(2)} disabled /> </td>
                            </tr>
                            <tr>
                                <td colSpan={5} className="fw-bold text-end border-0 text-success">
                                    TOTAL A COBRAR:
                                </td>
                                <td style={{ width: 150 }} className="border-0"> <input type="number" className="form-control text-end text-success border-success" id="ttotal" value={totalRounded} disabled /> </td>
                            </tr>
                            <tr>
                                <td colSpan={5} className="fw-bold text-end border-0 text-danger">
                                    FALTANTE:
                                </td>
                                <td style={{ width: 150 }} className="border-0"> <input type="number" className="form-control fw-bold text-end text-danger border-danger" value={faltanteRounded} disabled /> </td>
                            </tr>
                            <tr>
                                <td colSpan={5} className="fw-bold text-end border-0 fw-bold text-primary">
                                    CAMBIO:
                                </td>
                                <td style={{ width: 150 }} className="border-0"> <input type="number" className="form-control border-primary text-end fw-bold text-primary" value={cambioRounded} disabled /> </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <hr className="my-0" />
                <div className="mt-3">
                    <form className="row g-2 align-items-end" onSubmit={handleSubmit(onSubmit)}>
                        <div className="col-12 col-md-3">
                            <label className="form-label">Forma de pago:</label>
                            <select className={`form-select ${errors.metodo ? 'is-invalid' : ''}`} {...register("metodo", { required: true })}  >
                                <option value="">Seleccione una opción</option>
                                {
                                    // listPaymentMethods.map((item) => (<option key={item.ID} value={item.ID}> {item.CODE}- {item.nombre}</option>))
                                    listPaymentMethods.map((item) => (<option key={item.ID} value={item.ID}>{item.NOMBRE}</option>))
                                }
                            </select>
                        </div>

                        <div className="col-12 col-md-6">
                            <label className="form-label">Comentarios:</label>
                            <input type="text" className="form-control" {...register("comentarios")} />
                        </div>

                        <div className="col-12 col-md-2">
                            <label className="form-label">Monto:</label>
                            <input type="number" step="any" onKeyDown={InputValidators.validateDecimalKeyDown} className={`form-control ${errors.monto ? 'is-invalid' : ''}`}
                                {...register("monto", { required: true, min: 1, onBlur: (e) => InputValidators.formatDecimalOnBlur_ReactHook(e, "monto", setValue) })}
                            />
                        </div>

                        <div className="col-12 col-md-1 d-flex justify-content-md-center">
                            <button className="btn btn-outline-dark border-0 w-100" type="submit">
                                <FiPlusCircle size={22} />
                            </button>
                        </div>
                    </form>
                </div>

                <div className="row mt-2">
                    <div className='col-12'>
                        <Table
                        columns={columns}
                        data={pagos}
                        function_={(row) => console.log("Doble clic en:", row)}
                        placeholderSearch="Buscar orden"
                        showPagination={false}
                        showControls={false}
                        tableHeight="200px"
                        showTotals={false}
                        msgDisplay={'No se han agregado formas de pago'}
                    />
                    </div>
                </div>
            </div>
        </div>
    )
}
